import { SourceName } from "../sources/types";
// Global types for the app
import { DestinationKey } from "../destinations/types";
import { EventSource, EventType } from "../eventTypes";
import palette from "../theme/palette";
import typography from "../theme/typography";
import { SimplePaletteColorOptions } from "@material-ui/core";
import { ColorPartial, Palette } from "@material-ui/core/styles/createPalette";
import Typography from "material-ui/styles/typography";

export type EnvironmentID = string;

export type EnabledDestinationInfo = Record<string, boolean>;

export type FivetranGroup = {
  envID: string;
  groupID: string;
  destinationID: string;
};

export type EventDefinitionFilter = {
  readonly negate: boolean;
  readonly op: "=" | "contains" | "css_matches" | "wildcard_matches" | "any_of";
  readonly prop: string;
  readonly type: string;
  readonly val: string;
};

export type EventDefinitionFilters = {
  readonly type: string;
  readonly filters: EventDefinitionFilter[];
};

export type LoadedEventDefinition = {
  enabled_dests: EnabledDestinationInfo;
  enabled_client_destinations: EnabledDestinationInfo;
  enabled_server_destinations: EnabledDestinationInfo;
  deleted_at?: number;
  updated_at?: number;
  created_at?: number;
  created_by?: string;
  last_updated_by?: string;
  id: string;
  name: string;
  segment_compatible: boolean;
  source: EventSource;
  // TODO: every event def should have a type, but there are many in the DB that
  // have type = null. We should backfill every event def type and make sure all
  // new event defs have a type, and then this doesn't need to be nullable.
  type: EventType | null;
  filter: EventDefinitionFilters; // TODO
  is_hidden: boolean;
  description: string;
  tags: Record<string, true>;
  snapshots: Record<string, unknown>[];
};

export type DestinationConnectionMode = "client" | "server";

export enum SensitivityLevel {
  NoSensitivitySet = "NO_SENSITIVITY_SET",
  NotSensitive = "NOT_SENSITIVE",
  NotSensitiveIfHashed = "NOT_SENSITIVE_IF_HASHED",
}

export type PropertySensitivityLevels = Record<string, SensitivityLevel>;

export type LoadedDestinationInfo = {
  enabled: boolean;
  env_id: string;
  id: string;
  type_slug: string;
  is_valid: boolean;
  is_valid_client_side: boolean;
  is_valid_server_side: boolean;
  fivetran_group_id: string | null;
  name: DestinationKey;
  instance_name: string;
  settings: Record<string, any>;
  mapping?: Array<any>;
  // TODO CJ This will need to match existing destinations of the same type if the exist on create.
  has_baa: boolean;
  connection_mode: DestinationConnectionMode;
  function_id?: string;
  // TODO CJ The next three will need to match existing destinations of the same type if the exist on create.
  event_property_sensitivity_levels?: PropertySensitivityLevels;
  user_property_sensitivity_levels?: PropertySensitivityLevels;
  group_property_sensitivity_levels?: PropertySensitivityLevels;
};

export type LoadedSourceInfo = {
  env_id: string;
  name: SourceName;
  settings: Record<string, any>;
  last_sync?: LastSyncInfo;
  sync_in_progress: boolean;
  enabled: boolean;
  schedule: string;
  num_configured_tables: number;
};

export type LastSyncInfo = {
  last_sync_time: number;
  last_sync_failed: boolean;
  failure_reason?: string;
};

export type GetEventDefsResponse = {
  dest_info: LoadedDestinationInfo[];
  event_defs: LoadedEventDefinition[];
};

export type ListActiveDestinationsResponse = {
  destinations: LoadedDestinationInfo[];
};

type CustomTheme = {
  overrides: any;
  palette: typeof palette & { icon: string };
  shadows: string[];
  spacing: any;
  typography: typeof typography & Typography;
};

declare module "@material-ui/styles" {
  export interface DefaultTheme extends CustomTheme {}
}

interface CustomPaletteColorOptions extends SimplePaletteColorOptions {
  bright?: string;
  contrastText?: string;
  link?: string;
  subtle?: string;
}

declare module "@material-ui/core" {
  export interface PaletteColorOptions extends CustomPaletteColorOptions {}
}
